import { ListItemComponent } from '@app/features/shared/components/list-item/list-item.component'
import { BillingEntitlementState } from '@services/billing-entitlement-state.service'
import {
    AfterViewInit,
    ApplicationRef,
    ComponentRef,
    Directive,
    ElementRef,
    EnvironmentInjector,
    Input,
    OnDestroy,
    Renderer2,
    createComponent,
    inject,
} from '@angular/core'

@Directive({
    standalone: true,
})
export class BillingEntitlementPresentationDirective implements AfterViewInit, OnDestroy {

    private elementRef = inject(ElementRef<HTMLElement>)
    private renderer = inject(Renderer2)
    private applicationRef = inject(ApplicationRef)
    private environmentInjector = inject(EnvironmentInjector)
    private state: BillingEntitlementState = 'unlocked'
    private viewInitialized = false
    private lockedTitle?: string
    private lockedSubtitle?: string
    private lockedMessage?: string
    private lockBadge?: HTMLElement
    private lockedMessageElement?: HTMLElement
    private loadingSpinner?: HTMLElement
    private lockedListItem?: ComponentRef<ListItemComponent>
    private originalContent?: DocumentFragment

    @Input() set appBillingEntitlementLockedTitle(title: string | null | undefined) {
        this.lockedTitle = title ?? undefined
        this.updateLockedContent()
    }

    @Input() set appBillingEntitlementLockedSubtitle(subtitle: string | null | undefined) {
        this.lockedSubtitle = subtitle ?? undefined
        this.updateLockedContent()
    }

    @Input() set appBillingEntitlementLockedMessage(message: string | null | undefined) {
        this.lockedMessage = message ?? undefined
        this.updateLockedMessage()
    }

    ngAfterViewInit() {
        this.viewInitialized = true
        this.updateLockedContent()
    }

    ngOnDestroy() {
        this.clearLoadingState()
        this.clearLockedPresentation()
        this.destroyLockedListItem()
    }

    setState(state: BillingEntitlementState) {
        if (this.state === state) {
            return
        }

        this.clearLoadingState()
        this.clearLockedPresentation()
        this.state = state
        if (state === 'loading') {
            this.applyLoadingState()
        } else if (state === 'locked') {
            this.applyLockedPresentation()
        }
    }

    private applyLoadingState() {
        const host = this.elementRef.nativeElement
        this.renderer.addClass(host, 'billing-entitlement-loading')
        this.renderer.setAttribute(host, 'aria-busy', 'true')
        if (this.loadingSpinner === undefined) {
            this.loadingSpinner = this.createLoadingSpinner()
            this.renderer.appendChild(host, this.loadingSpinner)
        }
    }

    private clearLoadingState() {
        const host = this.elementRef.nativeElement
        this.renderer.removeClass(host, 'billing-entitlement-loading')
        this.renderer.removeAttribute(host, 'aria-busy')
        if (this.loadingSpinner) {
            this.renderer.removeChild(host, this.loadingSpinner)
            this.loadingSpinner = undefined
        }
    }

    private applyLockedPresentation() {
        const host = this.elementRef.nativeElement
        this.renderer.addClass(host, 'billing-entitlement-locked')
        this.updateLockedContent()
        this.updateLockedMessage()
        if (this.lockBadge === undefined) {
            this.lockBadge = this.createLockBadge()
            this.renderer.appendChild(host, this.lockBadge)
        }
    }

    private clearLockedPresentation() {
        const host = this.elementRef.nativeElement
        this.renderer.removeClass(host, 'billing-entitlement-locked')
        if (this.lockedMessageElement) {
            this.renderer.removeChild(host, this.lockedMessageElement)
            this.lockedMessageElement = undefined
        }
        if (this.lockBadge) {
            this.renderer.removeChild(host, this.lockBadge)
            this.lockBadge = undefined
        }
        this.restoreOriginalContent()
    }

    private updateLockedMessage() {
        if (this.state !== 'locked') {
            return
        }

        const host = this.elementRef.nativeElement
        if (this.lockedMessage === undefined) {
            if (this.lockedMessageElement) {
                this.renderer.removeChild(host, this.lockedMessageElement)
                this.lockedMessageElement = undefined
            }
            return
        }

        if (this.lockedMessageElement) {
            this.renderer.setProperty(this.lockedMessageElement, 'textContent', this.lockedMessage)
            return
        }

        this.lockedMessageElement = this.renderer.createElement('span')
        this.renderer.addClass(this.lockedMessageElement, 'billing-entitlement-form-control-message')
        this.renderer.appendChild(this.lockedMessageElement, this.renderer.createText(this.lockedMessage))
        this.renderer.appendChild(host, this.lockedMessageElement)
    }

    private updateLockedContent() {
        if (!this.viewInitialized || this.state !== 'locked') {
            return
        }
        if (this.lockedTitle === undefined && this.lockedSubtitle === undefined) {
            this.restoreOriginalContent()
            return
        }

        const host = this.elementRef.nativeElement
        if (this.lockedListItem === undefined) {
            this.originalContent = host.ownerDocument.createDocumentFragment()
            Array.from(host.childNodes)
                .filter(child => child !== this.lockBadge && child !== this.lockedMessageElement)
                .forEach(child => this.renderer.appendChild(this.originalContent, child))

            this.lockedListItem = createComponent(ListItemComponent, {
                environmentInjector: this.environmentInjector,
            })
            this.applicationRef.attachView(this.lockedListItem.hostView)
            this.renderer.appendChild(host, this.lockedListItem.location.nativeElement)
        }

        this.lockedListItem.setInput('title', this.lockedTitle ?? '')
        this.lockedListItem.setInput('subtitle', this.lockedSubtitle ?? '')
        this.lockedListItem.changeDetectorRef.detectChanges()
    }

    private restoreOriginalContent() {
        if (this.lockedListItem === undefined || this.originalContent === undefined) {
            return
        }

        const host = this.elementRef.nativeElement
        const lockedListItemElement = this.lockedListItem.location.nativeElement
        this.renderer.removeChild(host, lockedListItemElement)
        this.destroyLockedListItem()
        this.renderer.appendChild(host, this.originalContent)
        this.originalContent = undefined
    }

    private destroyLockedListItem() {
        if (this.lockedListItem === undefined) {
            return
        }
        this.applicationRef.detachView(this.lockedListItem.hostView)
        this.lockedListItem.destroy()
        this.lockedListItem = undefined
    }

    private createLockBadge(): HTMLElement {
        const badge = this.renderer.createElement('span')
        this.renderer.addClass(badge, 'billing-entitlement-lock-badge')

        const icon = this.renderer.createElement('i')
        this.renderer.addClass(icon, 'bi')
        this.renderer.addClass(icon, 'bi-lock-fill')
        this.renderer.appendChild(badge, icon)

        const label = this.renderer.createText('Upgrade')
        this.renderer.appendChild(badge, label)
        return badge
    }

    private createLoadingSpinner(): HTMLElement {
        const wrapper = this.renderer.createElement('div')
        this.renderer.addClass(wrapper, 'billing-entitlement-loading-spinner')

        const spinner = this.renderer.createElement('div')
        this.renderer.addClass(spinner, 'spinner-border')
        this.renderer.setAttribute(spinner, 'role', 'status')
        this.renderer.setAttribute(spinner, 'aria-label', 'Loading feature availability')
        this.renderer.appendChild(wrapper, spinner)
        return wrapper
    }
}
